import { useCallback, useEffect, useRef, useState } from "react";
import { HelpCircle, Pause, Play, Volume2, X } from "lucide-react";
import { toast } from "sonner";
import { API, api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const CARD_W = 380;

// whichever voice the member already listens to elsewhere
const chosenVoice = () =>
  localStorage.getItem("cml_tour_voice") || localStorage.getItem("chatmilu.brief.voice")
  || localStorage.getItem("cml_read_voice") || "nova";

const spot = (target) => {
  const el = document.querySelector(`[data-testid="${target}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (!r.width && !r.height) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
};

// MiLu walks a newcomer round a page: a spotlight on each control while she explains it.
export const PageGuide = ({ name }) => {
  const { lang } = useI18n();
  const seenKey = `cml_guide_${name}`;
  const [guide, setGuide] = useState(null);
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState(0);
  const [box, setBox] = useState(null);
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(false);
  const guideWords = useRef({});
  const sound = useRef(null);
  const timer = useRef(null);
  const session = useRef(`g_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`);

  useEffect(() => {
    api.get(`/guide/${name}`, { params: { lang } })
      .then(({ data }) => { setGuide(data); guideWords.current = data.ui || {}; })
      .catch((e) => console.error(`could not load the ${name} walkthrough`, e));
  }, [name, lang]);

  // arriving with ?tour=1 (from the Support shelf) plays it whether or not they have seen it
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("tour") !== "1") return;
    url.searchParams.delete("tour");
    window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    const meet = setTimeout(() => { setAt(0); setOpen(true); }, 900);
    return () => clearTimeout(meet);
  }, []);

  // the first time someone lands here, MiLu introduces the place herself
  useEffect(() => {
    if (localStorage.getItem(seenKey)) return;
    const meet = setTimeout(() => { setAt(0); setOpen(true); }, 1400);
    return () => clearTimeout(meet);
  }, [seenKey]);

  const steps = guide?.steps || [];
  const step = open ? steps[at] : null;
  const say = guide?.ui || {};

  const leave = useCallback((finished) => {
    clearTimeout(timer.current);
    sound.current?.pause();
    localStorage.setItem(seenKey, "1");
    api.post(`/guide/${name}/seen`,
             { session: session.current, step: at, lang, finished: !!finished, skipped: !finished })
      .then(({ data }) => {
        if (data?.reward) toast.success(guideWords.current.reward || "A free week of Premium is yours");
      })
      .catch(() => {});
    setOpen(false);
    setAt(0);
  }, [at, lang, name, seenKey]);

  // the spotlight follows the control, even while the page moves under it
  useEffect(() => {
    if (!step) return;
    const el = document.querySelector(`[data-testid="${step.target}"]`);
    if (el) el.scrollIntoView({ behavior: "instant", block: step.whole ? "start" : "center" });
    const tick = () => setBox(spot(step.target));
    tick();
    const beat = setInterval(tick, 200);
    return () => clearInterval(beat);
  }, [step]);

  // MiLu says her line, and the stop holds for as long as it takes her
  useEffect(() => {
    if (!step || paused) return;
    let gone = false;
    const onward = () => { if (!gone) setAt((i) => i + 1); };
    const holdUntil = Date.now() + step.seconds * 1000;
    const settle = () => {
      clearTimeout(timer.current);
      timer.current = setTimeout(onward, Math.max(150, holdUntil - Date.now()));
    };
    let audio;
    try {
      audio = new Audio(`${API}/guide/${name}/say/${at}?lang=${lang}&voice=${chosenVoice()}`);
    } catch {
      setMuted(true);
      settle();
      return () => clearTimeout(timer.current);
    }
    sound.current = audio;
    audio.onended = settle;
    audio.onerror = settle;
    audio.play().then(() => setMuted(false)).catch(() => { setMuted(true); settle(); });
    const guard = setTimeout(onward, step.seconds * 1000 + 9000);
    return () => {
      gone = true;
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
      clearTimeout(timer.current);
      clearTimeout(guard);
    };
  }, [at, step, paused, lang, name]);

  useEffect(() => {
    if (open && steps.length && at >= steps.length) leave(true);
  }, [at, open, steps.length, leave]);

  if (!open) {
    return (
      <button
        data-testid="page-guide-open"
        onClick={() => { setAt(0); setOpen(true); }}
        className="fixed bottom-5 start-5 z-[60] inline-flex min-h-[40px] items-center gap-2 rounded-full border border-white/15 bg-panel/90 px-4 text-xs text-ink-soft shadow-xl backdrop-blur transition-colors hover:border-brand/50 hover:text-brand"
      >
        <HelpCircle className="h-4 w-4" /> {say.how || "How this works"}
      </button>
    );
  }

  if (!step) {
    return (
      <div className="fixed bottom-5 end-5 z-[60] rounded-2xl border border-white/12 bg-panel/95 px-5 py-4 text-sm text-ink-soft backdrop-blur"
           data-testid="page-guide-loading">
        MiLu is writing this tour in your language…
      </div>
    );
  }

  const ring = box;
  const anchorTop = ring ? ring.top + ring.height + 16 : window.innerHeight / 2 - 90;
  const cardTop = ring && anchorTop + 210 > window.innerHeight
    ? Math.max(16, ring.top - 210) : anchorTop;
  const cardLeft = ring
    ? Math.min(Math.max(16, ring.left), window.innerWidth - CARD_W - 16)
    : Math.max(16, window.innerWidth / 2 - CARD_W / 2);

  return (
    <div
      className="fixed inset-0 z-[70] cursor-pointer"
      data-testid="page-guide"
      onClick={() => (at + 1 >= steps.length ? leave(true) : setAt(at + 1))}
    >
      {ring ? (
        <div
          className="pointer-events-none absolute rounded-2xl border-2 border-brand transition-all duration-300"
          style={{ top: ring.top - 6, left: ring.left - 6, width: ring.width + 12,
                   height: Math.min(ring.height + 12, window.innerHeight - 40),
                   boxShadow: "0 0 0 9999px rgba(4,6,12,0.78), 0 0 40px rgba(26,125,255,0.45)" }}
        />
      ) : (
        <div className="pointer-events-none absolute inset-0 bg-[rgba(4,6,12,0.78)]" />
      )}

      <div
        data-testid="page-guide-card"
        onClick={(e) => e.stopPropagation()}
        className="absolute w-[380px] max-w-[calc(100vw-32px)] cursor-default rounded-2xl border border-white/12 bg-panel/95 p-5 shadow-2xl backdrop-blur transition-all duration-300"
        style={{ top: cardTop, left: cardLeft }}
      >
        <p className="text-[11px] uppercase tracking-widest text-brand">
          {at + 1} {say.of} {steps.length} · {say.intro}
        </p>
        <p className="mt-1.5 font-display text-lg font-bold" data-testid="page-guide-title" dir="auto">
          {step.title}
        </p>
        <p className="mt-1.5 text-sm text-ink-soft" data-testid="page-guide-say" dir="auto">
          {step.say}
        </p>

        {muted && (
          <button
            data-testid="page-guide-sound"
            onClick={() => { setMuted(false); sound.current?.play().catch(() => setMuted(true)); }}
            className="mt-3 inline-flex min-h-[32px] items-center gap-2 rounded-full border border-brand/40 px-3 text-[11px] text-brand"
          >
            <Volume2 className="h-3.5 w-3.5" /> {say.muted}
          </button>
        )}

        <div className="mt-4 flex items-center gap-1.5">
          {steps.map((one, i) => (
            <button
              key={one.key}
              data-testid={`page-guide-dot-${one.key}`}
              onClick={() => setAt(i)}
              aria-label={one.title}
              className={`h-1.5 rounded-full transition-all ${
                i === at ? "w-6 bg-brand" : i < at ? "w-3 bg-brand/40" : "w-3 bg-white/15"}`}
            />
          ))}
          <div className="ms-auto flex items-center gap-2">
            <button
              data-testid="page-guide-pause"
              onClick={() => setPaused((p) => !p)}
              aria-label={paused ? say.next : say.skip}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/15 text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
            >
              {paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
            </button>
            <button
              data-testid="page-guide-back"
              onClick={() => setAt((i) => Math.max(0, i - 1))}
              disabled={at === 0}
              className="min-h-[32px] rounded-full border border-white/15 px-3 text-[11px] text-ink-soft transition-colors hover:border-brand/50 hover:text-brand disabled:opacity-40"
            >
              {say.back}
            </button>
            <button
              data-testid="page-guide-next"
              onClick={() => (at + 1 >= steps.length ? leave(true) : setAt(at + 1))}
              className="min-h-[32px] rounded-full bg-brand px-4 text-[11px] font-semibold text-white transition-colors hover:bg-brand-hover"
            >
              {at + 1 >= steps.length ? say.done : say.next}
            </button>
          </div>
        </div>
      </div>

      <button
        data-testid="page-guide-skip"
        onClick={(e) => { e.stopPropagation(); leave(false); }}
        className="absolute end-5 top-5 inline-flex min-h-[36px] items-center gap-2 rounded-full border border-white/15 bg-panel/90 px-4 text-xs text-ink-soft backdrop-blur transition-colors hover:border-brand/50 hover:text-brand"
      >
        <X className="h-3.5 w-3.5" /> {say.skip}
      </button>
    </div>
  );
};
